import type { ProjectResult } from '../api/types';
import { formatTime } from './utils';

export interface ResultRow {
  id?: string;
  url: string;
  success?: boolean;
  label: string;
  detail?: string;
  detailSummary?: string;
  httpStatus?: number | null;
  typeLabel?: string;
  hints?: string[];
  createdAt?: string;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function notificationTypeLabel(type: string | undefined): string | undefined {
  if (type === 'URL_UPDATED') return 'درخواست ایندکس';
  if (type === 'URL_DELETED') return 'حذف از نتایج';
  return undefined;
}

function notifyTimeFromPayload(payload: Record<string, unknown> | null): string | undefined {
  const response = asRecord(payload?.response);
  const meta = asRecord(response?.urlNotificationMetadata);
  const latest = asRecord(meta?.latestUpdate) ?? asRecord(meta?.latestRemove);
  return asString(latest?.notifyTime);
}

function guideSteps(payload: Record<string, unknown> | null): string[] {
  const guide = asRecord(payload?.guide);
  if (!Array.isArray(guide?.stepsFa)) return [];
  return guide.stepsFa.filter((step): step is string => typeof step === 'string' && step.trim().length > 0);
}

export function toResultRow(item: ProjectResult): ResultRow {
  const payload = asRecord(item.payload);
  const typeLabel = notificationTypeLabel(asString(payload?.type));
  const notifyTime = notifyTimeFromPayload(payload);
  const inspection = asRecord(payload?.inspection);
  const inspectionResult = asRecord(inspection?.inspectionResult);
  const indexStatus = asRecord(inspectionResult?.indexStatusResult);

  const hints: string[] = [];
  if (notifyTime) hints.push(`اعلان گوگل: ${formatTime(notifyTime)}`);

  const lastCrawl = asString(indexStatus?.lastCrawlTime);
  if (lastCrawl) hints.push(`تاریخ خزش گوگل: ${formatTime(lastCrawl)}`);

  const coverage = asString(indexStatus?.coverageState);
  if (coverage && coverage !== item.detail) hints.push(coverage);

  // `detail` is already derived from the same error/guide, so its steps would repeat it.
  if (!item.detail) hints.push(...guideSteps(payload).slice(0, 3));

  return {
    id: item.id,
    url: item.url,
    success: item.success,
    label: item.statusLabel || (item.success ? 'موفق' : 'ناموفق'),
    detail: item.detail ?? undefined,
    detailSummary: item.detailSummary ?? undefined,
    httpStatus: item.httpStatus,
    typeLabel,
    createdAt: item.createdAt,
    hints: hints.length > 0 ? hints : undefined,
  };
}
